/**
 * fontLicense/licenseApi.ts
 *
 * Thin wrappers over the /laws/* endpoints: the license-type dictionary, Jira URL
 * validation and the creation of the FontLicense records whose ids are then sent
 * with the upload.
 */

import { api } from '../apiScenarios';
import { LicenseInfo, LicenseType, toCreateFontLicensePayload } from './model';

/** Loads the license-type dictionary (sorted by name on the backend). */
export async function getLicenseTypes(): Promise<LicenseType[]> {
  const result = await api.laws.getLicenseTypes();
  if (!result.success) {
    throw new Error(result.data.error || 'Failed to load license types');
  }
  return result.data.response;
}

/** Outcome of a server-side Jira URL validation. */
export interface ValidateFontLicenseResult {
  valid: boolean;
  /** Backend error code on failure: invalid_jira_url. */
  code?: string;
}

/**
 * Validates a single Jira task URL against the backend
 * (POST /laws/validate_font_license). Only the format is checked — the same legal
 * task may cover several fonts, so the URL is intentionally not unique. On a 400
 * the backend returns { code, data }; we surface the code so the form can show a
 * precise message.
 */
export async function validateFontLicense(url: string): Promise<ValidateFontLicenseResult> {
  const result = await api.laws.validateFontLicense(url);
  if (result.success) {
    return { valid: result.data.response?.valid === true };
  }
  return { valid: false, code: result.data.response?.code };
}

/**
 * Creates the FontLicense record for one collected license
 * (POST /laws/font_licenses) and returns its id.
 *
 * The record is created without a node: it is attached to the file only when the
 * upload carrying its id completes. Records that never get attached (upload
 * cancelled or failed) stay unattached and are excluded from the moderation list,
 * so an abandoned form leaves nothing visible behind.
 */
export async function createFontLicense(info: LicenseInfo): Promise<number> {
  const result = await api.laws.createFontLicense(toCreateFontLicensePayload(info));
  if (!result.success) {
    throw new Error('Failed to create the font license');
  }
  return result.data.response.id;
}
